import { memo, useState, useMemo } from 'react';
import type { Block } from '@hump/shared-types';
import { useReaderStore, type ReadingSettings } from '../../stores/reader';
import { BlockActions } from './BlockActions';
import { FormulaBlock } from './FormulaBlock';
import { ImageAiButton } from './ImageAiButton';

/** 1.19 表格排序/复制：内联表格渲染组件 */
function TableRenderer({ tableData }: { tableData: { headers: string[]; rows: string[][] } }) {
  const { headers, rows } = tableData;
  const [sortCol, setSortCol] = useState<number | null>(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [copied, setCopied] = useState(false);

  // 按选中列排序后的行
  const sortedRows = useMemo(() => {
    if (sortCol === null) return rows;
    const cmp = (a: string, b: string) => {
      const na = Number(a);
      const nb = Number(b);
      if (!Number.isNaN(na) && !Number.isNaN(nb) && a.trim() !== '' && b.trim() !== '') return na - nb;
      return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    };
    return [...rows].sort((a, b) => {
      const av = a[sortCol] ?? '';
      const bv = b[sortCol] ?? '';
      const r = cmp(av, bv);
      return sortAsc ? r : -r;
    });
  }, [rows, sortCol, sortAsc]);

  function handleSort(i: number) {
    if (sortCol === i) {
      setSortAsc((v) => !v);
    } else {
      setSortCol(i);
      setSortAsc(true);
    }
  }

  function handleCopy() {
    // 1.19 复制为 TSV 格式（可直接粘贴到 Excel / Google Sheets）
    const tsv = [headers.join('\t'), ...sortedRows.map((r) => r.join('\t'))].join('\n');
    navigator.clipboard?.writeText(tsv).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div>
      <div className="flex justify-end mb-1">
        <button
          onClick={handleCopy}
          className="text-xs px-2 py-0.5 rounded border border-border-warm dark:border-border-warm-dark text-charcoal/70 dark:text-charcoal-light/70 hover:bg-warm-white dark:hover:bg-warm-dark transition-colors"
        >
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <div className="my-1 overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th
                  key={i}
                  onClick={() => handleSort(i)}
                  className="border border-border-warm dark:border-border-warm-dark px-3 py-1.5 bg-warm-white dark:bg-warm-dark text-charcoal dark:text-charcoal-light font-medium text-left cursor-pointer select-none hover:bg-border-warm/30 dark:hover:bg-border-warm-dark/30"
                  title="点击排序"
                >
                  {h}
                  {sortCol === i && <span className="ml-1">{sortAsc ? '▲' : '▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, ri) => (
              <tr key={ri}>
                {row.map((cell, ci) => (
                  <td key={ci} className="border border-border-warm dark:border-border-warm-dark px-3 py-1.5 text-charcoal/80 dark:text-charcoal-light/80">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface BlockViewProps {
  block: Block;
  showTranslation: boolean;
  docId?: string;
  onAskAi?: (blockId: string) => void;
}

function readStyle(settings: ReadingSettings): React.CSSProperties {
  return {
    fontSize: `${settings.fontSize}px`,
    lineHeight: settings.lineHeight,
  };
}

export const BlockView = memo(function BlockView({ block, showTranslation, docId, onAskAi }: BlockViewProps) {
  const settings = useReaderStore((s) => s.settings);
  const [imgZoomed, setImgZoomed] = useState(false);
  // #4 图片加载失败状态：显示占位文字，避免出现破损图标
  const [imgError, setImgError] = useState(false);

  const isHeading = block.type === 'heading';
  const headingSize = isHeading
    ? `${settings.fontSize + (block.level === 1 ? 8 : block.level === 2 ? 4 : 2)}px`
    : undefined;

  // 对齐 features.md 3.6：block 级状态标识
  // 已翻译 → oasis / 已批注 → camel / 已收藏 → info
  // 用左侧细彩色竖线标识（border-l-2）
  const statusBorder = (() => {
    if (block.transStatus === 'done') return 'border-l-2 border-oasis pl-2';
    if (block.annotations && block.annotations.length > 0) return 'border-l-2 border-camel pl-2';
    if (block.vocab && block.vocab.length > 0) return 'border-l-2 border-info pl-2';
    return 'border-l-2 border-transparent';
  })();

  return (
    <div
      data-block-id={block.id}
      className={`group py-2 ${statusBorder}`}
      style={{
        ...(isHeading ? { ...readStyle(settings), fontSize: headingSize, fontWeight: 600 } : readStyle(settings)),
        contentVisibility: 'auto',
        containIntrinsicSize: 'auto 100px',
      }}
    >
      {block.type === 'image' && block.imageData ? (
        // 图片渲染 + AI 分析按钮
        <figure className="my-3">
          <div className="relative">
            {imgError ? (
              // #4 图片加载失败占位：dataURL 损坏或过大时显示提示文字
              <div
                className="flex items-center justify-center rounded-lg border border-dashed border-border-warm dark:border-border-warm-dark text-charcoal/40 dark:text-charcoal-light/40 text-sm py-12"
                role="img"
                aria-label={block.caption || '图片加载失败'}
              >
                {block.caption ? block.caption : '图片无法显示'}
              </div>
            ) : (
              <img
                src={block.imageData}
                alt={block.caption || ''}
                className="max-w-full rounded-lg cursor-zoom-in"
                style={{ maxHeight: '500px', objectFit: 'contain' }}
                onClick={() => setImgZoomed(true)}
                onError={() => setImgError(true)}
                loading="lazy"
              />
            )}
            {!imgError && (
              <div className="absolute top-2 right-2">
                <ImageAiButton imageData={block.imageData} caption={block.caption} />
              </div>
            )}
          </div>
          {block.caption && (
            <figcaption className="text-sm text-charcoal/50 dark:text-charcoal-light/50 text-center mt-1">
              {block.caption}
            </figcaption>
          )}
          {imgZoomed && !imgError && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 cursor-zoom-out"
              onClick={() => setImgZoomed(false)}
            >
              <img
                src={block.imageData}
                alt={block.caption || ''}
                className="max-w-[90vw] max-h-[90vh] object-contain"
              />
            </div>
          )}
        </figure>
      ) : block.type === 'formula' && block.latex ? (
        <div>
          <FormulaBlock latex={block.latex} original={block.original} />
          {/* 对齐 features.md 1.21：公式AI入口 */}
          {docId && onAskAi && (
            <BlockActions docId={docId} blockId={block.id} original={block.original} transStatus={block.transStatus} onAskAi={onAskAi} />
          )}
        </div>
      ) : block.type === 'table' && block.tableData ? (
        // 表格渲染（1.19 表格排序/复制）
        <div>
          <TableRenderer tableData={block.tableData} />
          {/* 对齐 features.md 1.22：表格AI入口 */}
          {docId && onAskAi && (
            <BlockActions docId={docId} blockId={block.id} original={block.original} transStatus={block.transStatus} onAskAi={onAskAi} />
          )}
        </div>
      ) : isHeading ? (
        <BlockContent block={block} showTranslation={showTranslation} />
      ) : block.type === 'code' ? (
        <pre className="font-mono text-sm bg-warm-white dark:bg-warm-dark rounded-md p-3 overflow-x-auto">
          <code>{block.original}</code>
        </pre>
      ) : block.type === 'quote' ? (
        <blockquote className="border-l-2 border-oasis pl-3 italic text-charcoal/80 dark:text-charcoal-light/80">
          <BlockContent block={block} showTranslation={showTranslation} />
        </blockquote>
      ) : (
        <BlockContent block={block} showTranslation={showTranslation} />
      )}
      {block.type !== 'formula' && block.type !== 'table' && !(block.type === 'image' && block.imageData) && docId && onAskAi && (
        <BlockActions docId={docId} blockId={block.id} original={block.original} transStatus={block.transStatus} onAskAi={onAskAi} />
      )}
    </div>
  );
});

function BlockContent({ block, showTranslation }: { block: Block; showTranslation: boolean }) {
  return (
    <>
      <p className="text-charcoal dark:text-charcoal-light">{block.original}</p>
      {showTranslation && block.translation && (
        <p className="text-charcoal/70 dark:text-charcoal-light/70 mt-1">
          {block.translation}
        </p>
      )}
      {showTranslation && !block.translation && block.transStatus === 'none' && (
        <p className="text-charcoal/30 dark:text-charcoal-light/30 text-sm mt-1 italic">
          （未翻译）
        </p>
      )}
    </>
  );
}
